import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { LoginPage } from '@/pages/admin/LoginPage'
import { DashboardPage } from '@/pages/admin/DashboardPage'
import { AssociadosPage } from '@/pages/admin/AssociadosPage'
import { AssociadoPerfilPage } from '@/pages/admin/AssociadoPerfilPage'
import { VeiculosPage } from '@/pages/admin/VeiculosPage'
import { ContratosPage } from '@/pages/admin/ContratosPage'
import { ContratoDetailPage } from '@/pages/admin/ContratoDetailPage'
import { CancelamentosPage } from '@/pages/admin/CancelamentosPage'
import { FaturasPage } from '@/pages/admin/FaturasPage'
import { ServicosPage } from '@/pages/admin/ServicosPage'
import { ConfiguracoesPage } from '@/pages/admin/ConfiguracoesPage'
import { IntegracoesPage } from '@/pages/admin/IntegracoesPage'
import { NotificacoesPage } from '@/pages/admin/NotificacoesPage'
import { TemplatesPage } from '@/pages/admin/TemplatesPage'
import { NoticiasPage } from '@/pages/admin/NoticiasPage'
import { PushNotificationsPage } from '@/pages/admin/PushNotificationsPage'
import { AuditPage } from '@/pages/admin/AuditPage'
import { DlqPage } from '@/pages/admin/DlqPage'
import { TenantsPage } from '@/pages/admin/TenantsPage'
import { CobrancasPlacaPage } from '@/pages/admin/CobrancasPlacaPage'
import { ParceirosPage } from '@/pages/admin/ParceirosPage'
import { ContratosParceirosPage } from '@/pages/admin/ContratosParceirosPage'
import { ApiDocsPage } from '@/pages/admin/ApiDocsPage'
import { IntegracaoLogsPage } from '@/pages/admin/IntegracaoLogsPage'
import { FidelidadePage } from '@/pages/admin/FidelidadePage'
import { PortalLayout } from '@/components/portal/PortalLayout'
import { PortalDashboardPage } from '@/pages/portal/PortalDashboardPage'
import { PortalContratosPage } from '@/pages/portal/PortalContratosPage'
import { PortalFaturasPage } from '@/pages/portal/PortalFaturasPage'
import { PortalPerfilPage } from '@/pages/portal/PortalPerfilPage'
import { PortalLojaPage } from '@/pages/portal/PortalLojaPage'
import { PortalPontosPage } from '@/pages/portal/PortalPontosPage'
import { PortalLoginPage } from '@/pages/portal/PortalLoginPage'
import { PortalContratoDetailPage } from '@/pages/portal/PortalContratoDetailPage'
import { AssociateProtectedRoute } from '@/components/auth/AssociateProtectedRoute'
import { CheckoutPage } from '@/pages/checkout/CheckoutPage'
import { PortalValidacaoPage } from '@/pages/portal/PortalValidacaoPage'
import { PrivacyPolicyPage } from '@/pages/public/PrivacyPolicyPage'
import { TermsOfUsePage } from '@/pages/public/TermsOfUsePage'
import { Loader2 } from 'lucide-react'
import './index.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 min
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-50">
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={32} className="animate-spin text-primary-600" />
          <p className="text-sm text-surface-700/60">Carregando...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-950">
        <Loader2 size={32} className="animate-spin text-primary-400" />
      </div>
    )
  }

  if (isAuthenticated) {
    return <Navigate to="/admin" replace />
  }

  return <>{children}</>
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Public */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />

          {/* Admin (protected) */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="associados" element={<AssociadosPage />} />
            <Route path="associados/:id" element={<AssociadoPerfilPage />} />
            <Route path="veiculos" element={<VeiculosPage />} />
            <Route path="entidades" element={<TenantsPage />} />
            <Route path="contratos" element={<ContratosPage />} />
            <Route path="contratos/:id" element={<ContratoDetailPage />} />
            <Route path="cancelamentos" element={<CancelamentosPage />} />
            <Route path="faturas" element={<FaturasPage />} />
            <Route path="cobrancas-placa" element={<CobrancasPlacaPage />} />
            <Route path="servicos" element={<ServicosPage />} />
            <Route path="fidelidade" element={<FidelidadePage />} />
            <Route path="integracoes" element={<IntegracoesPage />} />
            <Route path="parceiros" element={<ParceirosPage />} />
            <Route path="parceiros/gestao" element={<ContratosParceirosPage />} />
            <Route path="api-docs" element={<ApiDocsPage />} />
            <Route path="integracao-logs" element={<IntegracaoLogsPage />} />
            <Route path="notificacoes">
              <Route index element={<NotificacoesPage />} />
              <Route path="templates" element={<TemplatesPage />} />
              <Route path="noticias" element={<NoticiasPage />} />
              <Route path="push" element={<PushNotificationsPage />} />
            </Route>
            <Route path="audit" element={<AuditPage />} />
            <Route path="dlq" element={<DlqPage />} />
            <Route path="settings" element={<ConfiguracoesPage />} />
          </Route>

          {/* Portal do Associado */}
          <Route path="/portal/login" element={<PortalLoginPage />} />
          <Route
            path="/portal"
            element={
              <AssociateProtectedRoute>
                <PortalLayout />
              </AssociateProtectedRoute>
            }
          >
            <Route index element={<PortalDashboardPage />} />
            <Route path="loja" element={<PortalLojaPage />} />
            <Route path="pontos" element={<PortalPontosPage />} />
            <Route path="contratos" element={<PortalContratosPage />} />
            <Route path="contratos/:id" element={<PortalContratoDetailPage />} />
            <Route path="faturas" element={<PortalFaturasPage />} />
            <Route path="perfil" element={<PortalPerfilPage />} />
          </Route>

          {/* Public Pages */}
          <Route path="/checkout/:servicoId" element={<CheckoutPage />} />
          <Route path="/validar/:id" element={<PortalValidacaoPage />} />
          <Route path="/privacidade" element={<PrivacyPolicyPage />} />
          <Route path="/termos" element={<TermsOfUsePage />} />

          {/* Default redirect to Portal */}
          <Route path="*" element={<Navigate to="/portal/login" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
